/**
 * S3 media-key derivation, shared by the web client and mobile.
 *
 * WHY THIS IS SHARED RATHER THAN COPIED (TRIL-628). Both surfaces have to turn a stored
 * `block.data.file.url` back into the S3 key that `deletePostMedia` authorizes on, and TRIL-615's
 * review found the cost of duplicating a derivation: the same fact was computed in two places, the
 * two disagreed, and the user was shown copy describing something that had not happened. The same
 * class turned up again as `draftOwnerId` (read path vs write path) in TRIL-622.
 *
 * The server deliberately does NOT use this. It authorizes on the key prefix and never parses a
 * URL, so giving it a URL parser would hand it a shape it has no business trusting.
 */

/**
 * Extract the S3 key from an absolute S3 URL. Returns null for anything that is not a usable
 * http(s) S3 URL — including the empty/relative/`s3://` states, which are the normal
 * "not uploaded yet" cases and not errors.
 */
export const extractS3Key = (
  imageUrl: string | null | undefined,
): string | null => {
  if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) return null;

  try {
    const url = new URL(imageUrl);

    // bucket.s3.region.amazonaws.com/<key>  — the format getPresignedUrl returns.
    if (url.hostname.includes('.s3.') && url.hostname.includes('.amazonaws.com')) {
      // The WHOLE path is the key. Since TRIL-617 that is `<userId>/<uuid>.<ext>`, not a bare
      // filename, and deletePostMedia authorizes on exactly that owner prefix — truncating here
      // would make every delete fail as "not yours".
      return url.pathname.substring(1) || null;
    }

    // s3.region.amazonaws.com/<bucket>/<key>
    if (url.hostname.startsWith('s3.') && url.hostname.includes('.amazonaws.com')) {
      const parts = url.pathname.substring(1).split('/');
      return parts.slice(1).join('/') || null;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Owner-bound keys are `<userId>/<uuid>.<ext>` (TRIL-617). Objects uploaded before that have no
 * owner segment, so nobody can prove the right to delete one and the server refuses them
 * permanently — callers must not advise a retry. Cleanup of those is TRIL-625.
 */
export const isOwnerBoundKey = (key: string): boolean => key.includes('/');
